import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Study statistics: the detailed numbers, kept off the calm study screen.

const hm = (min) => {
  const h = Math.floor((min || 0) / 60);
  const m = (min || 0) % 60;
  if (!h) return `${m}m`;
  return m ? `${h}h ${m}m` : `${h}h`;
};

// reading time is kept in seconds; shown as minutes ("under 1m" for a little)
const hmSec = (sec) => {
  const v = Math.max(0, sec || 0);
  if (v > 0 && v < 60) return '<1m';
  return hm(Math.floor(v / 60));
};

const BookIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#c9935a" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 6.5C10.3 5.2 7.8 4.5 4 4.5v13c3.8 0 6.3.7 8 2 1.7-1.3 4.2-2 8-2v-13c-3.8 0-6.3.7-8 2z" />
    <path d="M12 6.5v13" />
  </svg>
);

// a subject's colour, darker for gradients, or see-through
const shade = (hex, f = 0.68) => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return '#8a5f35';
  const n = parseInt(m[1], 16);
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255].map(v => Math.round(v * f).toString(16).padStart(2, '0')).join('')}`;
};
const tint = (hex, a) => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return `rgba(201,147,90,${a})`;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

const Paw = ({ size = 18, filled = true, color = '#c9935a' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <g fill={filled ? color : 'none'} stroke={color} strokeWidth={filled ? 0 : 1.4}>
      <ellipse cx="12" cy="16" rx="5" ry="4.2" />
      <ellipse cx="5.2" cy="10.2" rx="2.1" ry="2.6" />
      <ellipse cx="9.4" cy="6.2" rx="2.1" ry="2.7" />
      <ellipse cx="14.6" cy="6.2" rx="2.1" ry="2.7" />
      <ellipse cx="18.8" cy="10.2" rx="2.1" ry="2.6" />
    </g>
  </svg>
);

export default function StudyStats() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [stats, setStats] = useState(null);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalDraft, setGoalDraft] = useState('');
  const [reading, setReading] = useState(null);     // how much you've read, by day

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    load();
  }, []);

  const load = () => {
    axios.get(`${API}/study/stats?token=${token}`).then(res => setStats(res.data)).catch(() => {});
    axios.get(`${API}/reading/stats?token=${token}`).then(res => setReading(res.data)).catch(() => {});
  };

  // which subject you're looking at (remembered)
  const [subjKey, setSubjKey] = useState(() => { try { return localStorage.getItem('statsSubject') || ''; } catch (e) { return ''; } });
  const pickSubj = (k) => { setSubjKey(k); try { localStorage.setItem('statsSubject', k); } catch (e) { /* fine */ } };

  const subjects = stats?.subjects || [];
  const current = subjects.find(x => x.key === subjKey) || subjects.find(x => !x.hidden) || subjects[0] || null;
  const sub = (current && (stats?.per_subject || {})[current.key]) || stats || null;
  const accent = (current && current.color) || '#c9935a';
  const colorOf = (k) => (subjects.find(x => x.key === k) || {}).color || '#8a7466';
  const nameOf = (k) => (subjects.find(x => x.key === k) || {}).name || k;
  const order = (obj) => [...subjects.map(x => x.key).filter(k => (obj || {})[k]), ...Object.keys(obj || {}).filter(k => obj[k] && !subjects.some(x => x.key === k))];
  const bySubject = stats?.by_subject || {};

  const saveGoal = () => {
    const minutes = Math.round(Number(goalDraft) * 60);
    setEditingGoal(false);
    if (!Number.isFinite(minutes) || minutes < 15) return;
    axios.put(`${API}/study/goal`, { token, minutes, subject: current ? current.key : null }).then(load).catch(() => {});
  };

  const goal = sub?.goal_minutes || 120;
  const today = sub?.today_minutes || 0;
  const minForDay = stats?.streak_min_minutes || 25;
  const week = sub?.week || [];
  const weekMax = Math.max(60, ...week.map(d => d.minutes));
  const readWeekMax = Math.max(3600, ...(reading?.week || []).map(d => d.seconds));
  const fillOf = { background: `linear-gradient(90deg, ${shade(accent)}, ${accent})` };
  const allKeys = order(bySubject.total);

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap" rel="stylesheet" />
      <header style={s.header}>
        <button style={s.back} onClick={() => navigate('/study')} aria-label="back">←</button>
        <div style={s.title}>statistics</div>
        {subjects.length > 1 && (
          <span style={s.subjectPick}>
            <span style={{ ...s.dot, background: accent }} />
            <select style={{ ...s.subjectSelect, borderColor: tint(accent, 0.35) }} value={current ? current.key : ''}
              onChange={e => pickSubj(e.target.value)} aria-label="subject">
              {subjects.map(x => <option key={x.key} value={x.key}>{x.name}</option>)}
            </select>
          </span>
        )}
      </header>

      <main style={s.main}>
        {/* TODAY, AGAINST THIS SUBJECT'S GOAL */}
        <section style={s.card}>
          <div style={s.top}>
            <div>
              <div style={s.big}>{hm(today)}</div>
              <div style={s.label}>
                today ·{' '}
                {editingGoal ? (
                  <>
                    <input
                      autoFocus style={s.goalInput} inputMode="decimal" value={goalDraft}
                      onChange={e => setGoalDraft(e.target.value)} onBlur={saveGoal}
                      onKeyDown={e => e.key === 'Enter' && saveGoal()} aria-label="daily goal in hours"
                    /> hours
                  </>
                ) : (
                  <button style={{ ...s.link, color: accent }} onClick={() => { setGoalDraft(String(goal / 60)); setEditingGoal(true); }}>
                    goal {hm(goal)}
                  </button>
                )}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={s.big}>{hm(sub?.week_avg_minutes || 0)}</div>
              <div style={s.label}>avg a day this week</div>
            </div>
          </div>
          <div style={s.bar}><div style={{ ...s.fill, ...fillOf, width: `${Math.min(100, (today / goal) * 100)}%` }} /></div>
        </section>

        {/* THIS SUBJECT'S STREAK */}
        <section style={s.card}>
          <div style={s.streakTop}>
            <span style={s.streakNum}>
              <Paw size={20} color={accent} /> {sub?.streak || 0}
              <span style={s.unit}>{(sub?.streak || 0) === 1 ? ' day' : ' days'} in a row</span>
            </span>
            <span style={s.label}>best {sub?.best_streak || 0}</span>
          </div>
          <div style={s.paws}>
            {week.map(d => {
              const hit = d.minutes >= minForDay;
              return (
                <div key={d.date} style={s.pawCol} title={`${d.weekday}: ${hm(d.minutes)}`}>
                  <Paw size={22} filled={hit} color={hit ? accent : tint(accent, 0.28)} />
                  <div style={s.day}>{d.weekday.slice(0, 2).toLowerCase()}</div>
                </div>
              );
            })}
          </div>
          <div style={s.hint}>a paw fills in on days you study {current && subjects.length > 1 ? `${current.name} for ` : ''}{minForDay}+ minutes</div>
        </section>

        {/* THE LAST 7 DAYS */}
        <section style={s.card}>
          <div style={s.label}>the last 7 days</div>
          <div style={s.week}>
            {week.map(d => (
              <div key={d.date} style={s.weekCol} title={`${d.weekday}: ${hm(d.minutes)}`}>
                <div style={s.weekVal}>{d.minutes ? hm(d.minutes) : ''}</div>
                <div style={s.track}>
                  <div style={{ ...s.weekFill, background: `linear-gradient(180deg, ${accent}, ${shade(accent)})`, height: `${Math.max(d.minutes ? 6 : 0, (d.minutes / weekMax) * 100)}%` }} />
                </div>
                <div style={s.day}>{d.weekday.slice(0, 2).toLowerCase()}</div>
              </div>
            ))}
          </div>
        </section>

        <section style={{ ...s.card, ...s.three }}>
          <div><div style={{ ...s.mid, color: accent }}>{hm(sub?.week_total_minutes || 0)}</div><div style={s.label}>this week</div></div>
          <div><div style={{ ...s.mid, color: accent }}>{hm(sub?.month_minutes || 0)}</div><div style={s.label}>this month</div></div>
          <div><div style={{ ...s.mid, color: accent }}>{hm(sub?.total_minutes || 0)}</div><div style={s.label}>together, all time</div></div>
        </section>

        {/* EVERY SUBJECT TOGETHER — what Caleb's gifts count */}
        {allKeys.length > 1 && (
          <section style={s.card}>
            <div style={s.togetherTop}>
              <span style={s.label}>together, every subject</span>
              <span style={s.togetherNum}>{hm(stats?.total_minutes || 0)}</span>
            </div>
            <div style={s.splitBar}>
              {allKeys.map(k => <div key={k} style={{ flex: `${bySubject.total[k]} 0 0`, background: colorOf(k) }} title={`${nameOf(k)}: ${hm(bySubject.total[k])}`} />)}
            </div>
            <div style={s.legend}>
              {allKeys.map(k => (
                <span key={k} style={s.legendItem}><span style={{ ...s.dot, background: colorOf(k) }} />{nameOf(k)} <span style={s.legendVal}>{hm(bySubject.total[k])}</span></span>
              ))}
            </div>
          </section>
        )}

        {/* READING — what you spent of the time you earned (hidden when reading time is switched off) */}
        {reading && reading.visible && (
          <>
            <div style={s.sectionLabel}>reading</div>

            <section style={s.card}>
              <div style={s.top}>
                <div>
                  <div style={s.big}>{hmSec(reading.today_seconds)}</div>
                  <div style={s.label}>read today{reading.reading_now ? ' · reading now' : ''}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={s.big}>{hmSec(reading.week_avg_seconds)}</div>
                  <div style={s.label}>avg a day this week</div>
                </div>
              </div>
              <div style={s.bankRow}>
                <span style={s.bankText}><BookIcon size={15} /> {hmSec(reading.balance_seconds)} left to read</span>
              </div>
            </section>

            <section style={s.card}>
              <div style={s.label}>read, the last 7 days</div>
              <div style={s.week}>
                {(reading.week || []).map(d => (
                  <div key={d.date} style={s.weekCol} title={`${d.weekday}: ${hmSec(d.seconds)} read`}>
                    <div style={s.weekVal}>{d.seconds ? hmSec(d.seconds) : ''}</div>
                    <div style={s.track}>
                      <div style={{ ...s.weekFill, height: `${Math.max(d.seconds ? 6 : 0, (d.seconds / readWeekMax) * 100)}%` }} />
                    </div>
                    <div style={s.day}>{d.weekday.slice(0, 2).toLowerCase()}</div>
                  </div>
                ))}
              </div>
            </section>

            <section style={{ ...s.card, ...s.three }}>
              <div><div style={s.mid}>{hmSec(reading.week_total_seconds)}</div><div style={s.label}>this week</div></div>
              <div><div style={s.mid}>{hmSec(reading.month_seconds)}</div><div style={s.label}>this month</div></div>
              <div><div style={s.mid}>{hmSec(reading.total_read_seconds)}</div><div style={s.label}>read, all time</div></div>
            </section>
            <div style={s.hint}>
              {hmSec(reading.total_earned_seconds)} earned by studying so far · {reading.days_read} {reading.days_read === 1 ? 'day' : 'days'} with reading
              {order(reading.earned_by_subject).length > 1 && (
                <div style={{ ...s.split, justifyContent: 'center', marginTop: '6px' }}>
                  {order(reading.earned_by_subject).map(k => (
                    <span key={k} style={s.splitItem}><span style={{ ...s.dot, background: colorOf(k) }} />{nameOf(k)} {hmSec(reading.earned_by_subject[k])}</span>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const s = {
  // its own scrolling area, like your other pages (the site stops the page itself from scrolling)
  page: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#0e0b0f', color: '#e8ddd0', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 },
  header: {
    position: 'sticky', top: 0, zIndex: 10, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '12px',
    background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)', borderBottom: '1px solid rgba(201,147,90,0.06)',
  },
  back: { width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', color: '#c9935a', fontSize: '16px', cursor: 'pointer', flexShrink: 0 },
  title: { flex: 1, fontFamily: "'Cormorant Garamond', serif", fontSize: '21px', fontStyle: 'italic' },
  main: { maxWidth: '520px', margin: '0 auto', padding: '20px 20px 80px', display: 'flex', flexDirection: 'column', gap: '12px', boxSizing: 'border-box' },
  card: { background: '#17131b', border: '1px solid rgba(201,147,90,0.08)', borderRadius: '20px', padding: '18px' },
  top: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '12px' },
  big: { fontFamily: "'Cormorant Garamond', serif", fontSize: '32px', lineHeight: 1, fontVariantNumeric: 'tabular-nums' },
  mid: { fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', lineHeight: 1, color: '#c9935a', fontVariantNumeric: 'tabular-nums' },
  label: { fontSize: '11.5px', color: '#8a7466', marginTop: '5px' },
  link: { background: 'none', border: 'none', padding: 0, color: '#c9935a', fontSize: '11.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", textDecoration: 'underline', textUnderlineOffset: '2px' },
  goalInput: { width: '44px', background: '#0e0b0f', border: '1px solid rgba(201,147,90,0.3)', borderRadius: '6px', color: '#e8ddd0', padding: '1px 6px', fontSize: '12px', fontFamily: "'DM Sans', sans-serif" },
  bar: { height: '6px', borderRadius: '3px', background: 'rgba(201,147,90,0.1)', marginTop: '12px', overflow: 'hidden' },
  fill: { height: '100%', borderRadius: '3px', background: 'linear-gradient(90deg, #8a5f35, #c9935a)' },
  streakTop: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px' },
  streakNum: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', lineHeight: 1 },
  unit: { fontFamily: "'DM Sans', sans-serif", fontSize: '12px', color: '#8a7466', marginLeft: '2px' },
  paws: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px', marginTop: '14px' },
  pawCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' },
  day: { fontSize: '10.5px', color: '#6d5d4e' },
  hint: { fontSize: '10.5px', color: '#6d5d4e', marginTop: '10px', textAlign: 'center' },
  week: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px', marginTop: '12px', height: '130px' },
  weekCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' },
  weekVal: { fontSize: '9.5px', color: '#8a7466', minHeight: '12px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  track: { flex: 1, width: '100%', maxWidth: '26px', background: 'rgba(201,147,90,0.06)', borderRadius: '6px', display: 'flex', alignItems: 'flex-end', overflow: 'hidden' },
  weekFill: { width: '100%', borderRadius: '6px', background: 'linear-gradient(180deg, #c9935a, #8a5f35)' },
  sectionLabel: { fontSize: '10px', color: '#6d5d4e', letterSpacing: '0.12em', textTransform: 'uppercase', padding: '14px 2px 0' },
  bankRow: { marginTop: '14px', paddingTop: '12px', borderTop: '1px solid rgba(201,147,90,0.08)' },
  bankText: { display: 'inline-flex', alignItems: 'center', gap: '7px', fontFamily: "'Cormorant Garamond', serif", fontSize: '17px', color: '#e8ddd0' },
  stack: { width: '100%', display: 'flex', flexDirection: 'column-reverse', borderRadius: '6px', overflow: 'hidden' },
  legend: { display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '6px 16px', marginTop: '12px', fontSize: '11.5px', color: '#a8927f' },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: '6px' },
  legendVal: { color: '#6d5d4e' },
  dot: { width: '8px', height: '8px', borderRadius: '50%', display: 'inline-block', flexShrink: 0 },
  split: { display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '7px', fontSize: '10.5px', color: '#8a7466' },
  splitItem: { display: 'inline-flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' },
  goalNote: { fontSize: '11px', color: '#6d5d4e', marginTop: '9px' },
  subjectPick: { display: 'inline-flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
  subjectSelect: { background: '#120f18', border: '1px solid rgba(201,147,90,0.3)', borderRadius: '12px', padding: '7px 10px', color: '#e8ddd0', fontSize: '13px', fontFamily: "'DM Sans', sans-serif", outline: 'none', cursor: 'pointer', maxWidth: '46vw' },
  togetherTop: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px' },
  togetherNum: { fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', color: '#e8ddd0' },
  splitBar: { display: 'flex', height: '6px', borderRadius: '3px', overflow: 'hidden', marginTop: '12px', gap: '2px' },
  three: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' },
};
